/**
 * Storage Manager for Analytics
 * Handles persistent storage, caching, and data management
 */

import { compressEvents, decompressEvents, createEventBatches } from '../utils/dataProcessor';

class StorageManager {
  constructor() {
    this.storagePrefix = 'analytics_';
    this.maxStorageSize = 5 * 1024 * 1024; // 5MB limit
    this.compressionEnabled = true;
    this.batchSize = 50;
  }

  /**
   * Store events in local storage with compression
   */
  storeEvents(events) {
    if (!Array.isArray(events) || events.length === 0) return;

    try {
      const key = `${this.storagePrefix}pending_events`;
      const existingEvents = this.getStoredEvents();
      const allEvents = [...existingEvents, ...events];
      
      // Compress events if enabled
      const eventsToStore = this.compressionEnabled 
        ? compressEvents(allEvents)
        : allEvents;

      // Check storage size limit
      const dataSize = new Blob([JSON.stringify(eventsToStore)]).size;
      if (dataSize > this.maxStorageSize) {
        const reducedEvents = this.reduceStorageSize(eventsToStore);
        localStorage.setItem(key, JSON.stringify(reducedEvents));
      } else {
        localStorage.setItem(key, JSON.stringify(eventsToStore));
      }

      console.debug(`Stored ${events.length} events, total: ${allEvents.length}`);
    } catch (error) {
      console.error('Failed to store events:', error);
      this.clearStoredEvents();
      this.storeEvents(events);
    }
  }

  /**
   * Retrieve stored events from local storage
   */
  getStoredEvents() {
    try {
      const key = `${this.storagePrefix}pending_events`;
      const stored = localStorage.getItem(key);
      
      if (!stored) return [];

      const events = JSON.parse(stored);
      
      return this.compressionEnabled && events.length > 0 && events[0].e
        ? decompressEvents(events)
        : events;
    } catch (error) {
      console.error('Failed to retrieve stored events:', error);
      return [];
    }
  }

  /**
   * Clear stored events
   */
  clearStoredEvents() {
    try {
      const key = `${this.storagePrefix}pending_events`;
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to clear stored events:', error);
    }
  }

  /**
   * Remove specific events from storage
   */
  removeEvents(eventsToRemove) {
    try {
      const storedEvents = this.getStoredEvents();
      const eventIds = eventsToRemove.map(e => this.getEventId(e));
      
      const remainingEvents = storedEvents.filter(event => 
        !eventIds.includes(this.getEventId(event))
      );
      
      if (remainingEvents.length === 0) {
        this.clearStoredEvents();
      } else {
        const key = `${this.storagePrefix}pending_events`;
        const eventsToStore = this.compressionEnabled 
          ? compressEvents(remainingEvents)
          : remainingEvents;
        localStorage.setItem(key, JSON.stringify(eventsToStore));
      }
    } catch (error) {
      console.error('Failed to remove events:', error);
    }
  }

  /**
   * Store session data
   */
  storeSessionData(sessionId, data) {
    try {
      const key = `${this.storagePrefix}session_${sessionId}`;
      sessionStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to store session data:', error);
    }
  }

  /**
   * Retrieve session data
   */
  getSessionData(sessionId) {
    try {
      const key = `${this.storagePrefix}session_${sessionId}`;
      const stored = sessionStorage.getItem(key);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Failed to retrieve session data:', error);
      return null;
    }
  }

  /**
   * Store user preferences
   */
  storeUserPreferences(userId, preferences) {
    try {
      const key = `${this.storagePrefix}user_prefs_${userId}`;
      localStorage.setItem(key, JSON.stringify(preferences));
    } catch (error) {
      console.error('Failed to store user preferences:', error);
    }
  }

  /**
   * Retrieve user preferences
   */
  getUserPreferences(userId) {
    try {
      const key = `${this.storagePrefix}user_prefs_${userId}`;
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Failed to retrieve user preferences:', error);
      return null;
    }
  }

  /**
   * Store analytics metadata
   */
  storeMetadata(key, data) {
    try {
      const fullKey = `${this.storagePrefix}meta_${key}`;
      localStorage.setItem(fullKey, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Failed to store metadata:', error);
    }
  }

  /**
   * Retrieve analytics metadata
   */
  getMetadata(key) {
    try {
      const fullKey = `${this.storagePrefix}meta_${key}`;
      const stored = localStorage.getItem(fullKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.data;
      }
      return null;
    } catch (error) {
      console.error('Failed to retrieve metadata:', error);
      return null;
    }
  }

  /**
   * Get storage usage information
   */
  getStorageInfo() {
    try {
      let totalSize = 0;
      let itemCount = 0;
      const items = {};

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.storagePrefix)) {
          const value = localStorage.getItem(key);
          const size = new Blob([value]).size;
          totalSize += size;
          itemCount++;
          items[key] = { size, sizeFormatted: this.formatBytes(size) };
        }
      }

      return {
        totalSize,
        totalSizeFormatted: this.formatBytes(totalSize),
        itemCount,
        items,
        usagePercentage: (totalSize / this.maxStorageSize) * 100
      };
    } catch (error) {
      console.error('Failed to get storage info:', error);
      return { totalSize: 0, itemCount: 0, items: {} };
    }
  }

  /**
   * Reduce storage size by removing oldest events
   */
  reduceStorageSize(events) {
    const sortedEvents = [...events].sort((a, b) => {
      const timestampA = a.properties?.timestamp || a.p?.ts || 0;
      const timestampB = b.properties?.timestamp || b.p?.ts || 0;
      return new Date(timestampB) - new Date(timestampA);
    });

    let currentSize = 0;
    const keptEvents = [];

    for (const event of sortedEvents) {
      const eventSize = new Blob([JSON.stringify(event)]).size;
      if (currentSize + eventSize < this.maxStorageSize * 0.8) { // Use 80% of max size
        keptEvents.push(event);
        currentSize += eventSize;
      } else {
        break;
      }
    }
    return keptEvents;
  }

  /**
   * Generate unique event ID for deduplication
   */
  getEventId(event) {
    const timestamp = event.properties?.timestamp || event.p?.ts || Date.now();
    const eventName = event.eventName || event.e || 'unknown';
    const userId = event.properties?.userId || event.p?.uid || 'anonymous';
    return `${userId}_${eventName}_${timestamp}`;
  }

  /**
   * Clean up expired data based on retention policy
   */
  cleanupExpiredData(retentionDays = 30) {
    try {
      const cutoffTime = Date.now() - (retentionDays * 24 * 60 * 60 * 1000);
      
      // Clean events
      const events = this.getStoredEvents();
      const validEvents = events.filter(event => {
        const timestamp = event.properties?.timestamp || event.p?.ts;
        return timestamp && new Date(timestamp).getTime() > cutoffTime;
      });

      if (validEvents.length !== events.length) {
        const key = `${this.storagePrefix}pending_events`;
        const eventsToStore = this.compressionEnabled 
          ? compressEvents(validEvents)
          : validEvents;
        localStorage.setItem(key, JSON.stringify(eventsToStore));
        console.info(`Cleaned up ${events.length - validEvents.length} expired events`);
      }

      // Clean metadata
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith(`${this.storagePrefix}meta_`)) {
          try {
            const stored = localStorage.getItem(key);
            const data = JSON.parse(stored);
            if (data.timestamp < cutoffTime) {
              localStorage.removeItem(key);
            }
          } catch (error) {
            // Remove corrupted metadata
            localStorage.removeItem(key);
          }
        }
      }
    } catch (error) {
      console.error('Failed to cleanup expired data:', error);
    }
  }

  /**
   * Create backup of analytics data
   */
  createBackup() {
    try {
      const backup = {
        events: this.getStoredEvents(),
        metadata: {},
        timestamp: Date.now()
      };
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(`${this.storagePrefix}meta_`)) {
          backup.metadata[key] = localStorage.getItem(key);
        }
      }

      return backup;
    } catch (error) {
      console.error('Failed to create backup:', error);
      return null;
    }
  }

  /**
   * Restore from backup
   */
  restoreFromBackup(backup) {
    try {
      if (backup.events) {
        this.clearStoredEvents();
        this.storeEvents(backup.events);
      }

      if (backup.metadata) {
        Object.keys(backup.metadata).forEach(key => {
          localStorage.setItem(key, backup.metadata[key]);
        });
      }

      console.info('Analytics data restored from backup');
      return true;
    } catch (error) {
      console.error('Failed to restore from backup:', error);
      return false;
    }
  }

  /**
   * Format bytes to human readable format
   */
  formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Clear all analytics data from storage
   */
  clearAllData() {
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.storagePrefix)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(this.storagePrefix)) {
          sessionStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.error('Failed to clear all data:', error);
    }
  }
}

export default StorageManager;
